import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import RefreshIcon from '@mui/icons-material/Refresh'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Icon from '@mui/material/Icon'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import { styled } from '@mui/material/styles'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import React from 'react'
import { AutoSizer, List, type ListRowRenderer } from 'react-virtualized'

import IconButton from '@/app/component/atom/icon-button'
import MenuItem from '@/app/component/atom/menu-item'
import SearchBar from '@/app/component/atom/search-bar'
import StyledBadge from '@/app/component/atom/styled-badge'
import CustomTooltip from '@/app/component/atom/tooltip'
import Loading from '@/app/component/guard/loading'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { useDebouncedFn, useDeepState } from '@/app/helpers'
import { useDeleteGroup } from '@/app/module/groups/hooks/use-delete-group'
import { useGetGroupOptions } from '@/app/module/groups/hooks/use-get-group-options'
import { GroupsQuery } from '@/app/module/groups/hooks/use-get-groups'
import { useGetSegmentOptions } from '@/app/module/segments/hooks/use-get-segment-options'
import { SegmentsQuery } from '@/app/module/segments/hooks/use-get-segments'
import { numberWithCommas } from '@/app/service/util/format'
import { GroupOption } from '@/app/types'

enum GroupsTab {
  Groups = 'groups',
  Segments = 'segments',
}

type GroupListProps = {
  defaultSearchTerm: string
  disabled: boolean
  groups: GroupOption[]
  selected: string
  tab: GroupsTab

  onClick: (value: string) => () => void
  onCopy: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (id: string) => void
  onRefresh: () => void
  onSearch: (value: string) => void
}

const GroupList: React.FC<GroupListProps> = ({
  defaultSearchTerm,
  disabled,
  groups,
  selected,
  tab,
  onClick,
  onCopy,
  onDelete,
  onEdit,
  onRefresh,
  onSearch,
}) => {
  const [searchTerm, setSearchTerm] = useDeepState(defaultSearchTerm)
  const [menu, setMenu] = React.useState<{ anchor: HTMLElement; id: string } | null>(null)
  const menuId = React.useId()

  const onSearchChange = (value: string) => {
    setSearchTerm(value)
    onSearch(value)
  }

  const renderedGroups = React.useMemo(() => {
    if (!groups?.length) {
      return (
        <div
          style={{
            textAlign: 'center',
            paddingTop: '20px',
            paddingBottom: '20px',
          }}
        >
          <Typography>No {tab} found.</Typography>
        </div>
      )
    }

    const renderRow: ListRowRenderer = ({ index, key, style }) => {
      const { label, value, numberOfContacts, recounting = false } = groups[index]
      const labelId = `checkbox-list-label-${value}`

      const isSegment = tab === GroupsTab.Segments

      return (
        <StyledListItem
          key={key}
          secondaryAction={
            <IconButton
              edge="end"
              aria-label="chevronright"
              tooltip={`Open ${isSegment ? 'segment' : 'group'}`}
              onClick={onClick(value)}
            >
              {recounting ? <CircularProgress size={20} /> : <ChevronRightIcon className="open-segment" />}
            </IconButton>
          }
          style={style}
          title={label}
        >
          <StyledListItemButton disabled={recounting} selected={selected === value} onClick={onClick(value)}>
            <ListItemIcon>
              <IconButton
                aria-controls={menu?.id === value ? menuId : undefined}
                aria-expanded={menu?.id === value ? true : undefined}
                aria-haspopup="menu"
                aria-label={`Actions for ${label}`}
                disabled={disabled || recounting}
                id={`${menuId}-${value}`}
                tooltip={isSegment ? 'Segment actions' : 'Group actions'}
                onClick={(event) => {
                  event.stopPropagation()
                  setMenu({ anchor: event.currentTarget, id: value })
                }}
              >
                <MoreVertIcon fontSize="small" />
              </IconButton>
            </ListItemIcon>
            <ListItemText
              id={labelId}
              primary={<PrimaryLabel>{label}</PrimaryLabel>}
              secondary={`${numberWithCommas(numberOfContacts)} contact${numberOfContacts !== 1 ? 's' : ''}`}
            />
          </StyledListItemButton>
        </StyledListItem>
      )
    }

    return (
      <DrawerListWrapper className={`${tab}-list`}>
        <AutoSizer>
          {({ height, width }) => (
            <List
              height={height}
              overscanRowCount={10}
              rowCount={groups.length}
              rowHeight={ROW_HEIGHT}
              style={{
                paddingBottom: '8px',
                paddingTop: '8px',
                outline: 'none',
              }}
              width={width}
              rowRenderer={renderRow}
            />
          )}
        </AutoSizer>
      </DrawerListWrapper>
    )
  }, [disabled, groups, menu, menuId, selected, tab, onClick])

  return (
    <>
      <GroupSearchContainer>
        <SearchBarWrapper>
          <SearchBar
            data-testid={`${tab}-search-input`}
            id={`${tab}-search-input`}
            searchTerm={searchTerm}
            onChange={onSearchChange}
          />
        </SearchBarWrapper>
        <CustomTooltip title={`Refresh ${tab}`}>
          <IconButton aria-label={`refresh-${tab}`} onClick={onRefresh} size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </CustomTooltip>
      </GroupSearchContainer>
      {renderedGroups}
      <Menu
        anchorEl={menu?.anchor}
        id={menuId}
        MenuListProps={{ 'aria-labelledby': menu ? `${menuId}-${menu.id}` : undefined }}
        open={!!menu}
        onClose={() => setMenu(null)}
      >
        <MenuItem
          onClick={() => {
            if (menu) onEdit(menu.id)
            setMenu(null)
          }}
        >
          Edit
        </MenuItem>
        {tab === GroupsTab.Segments && (
          <MenuItem
            onClick={() => {
              if (menu) onCopy(menu.id)
              setMenu(null)
            }}
          >
            Copy
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (menu) onDelete(menu.id)
            setMenu(null)
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </>
  )
}

type Props = {
  selected?: string

  onAdd: (type: GroupsTab) => void
  onCopy: (id: number) => void
  onDeleted: (type: GroupsTab, id: number) => void
  onEdit: (type: GroupsTab, id: number) => void
  onFilterChange: (args: { name: GroupsTab; value: string }) => void
}

const Groups: React.FC<Props> = ({
  selected = '',

  onAdd,
  onCopy,
  onDeleted,
  onEdit,
  onFilterChange,
}) => {
  const [groupsQuery, setGroupsQuery] = React.useState<GroupsQuery>({
    page: 1,
  })
  const [segmentsQuery, setSegmentsQuery] = React.useState<SegmentsQuery>({
    page: 1,
  })
  const createConfirmation = useConfirmation()
  const onDeletedRef = React.useRef(onDeleted)
  React.useEffect(() => {
    onDeletedRef.current = onDeleted
  }, [onDeleted])

  const [tab, setTab] = React.useState<GroupsTab>(GroupsTab.Groups)
  const [deleteGroup, { isLoading: isDeleting }] = useDeleteGroup()

  const { data: groups, isFetching: isGroupsFetching, refetch: refetchGroups } = useGetGroupOptions(groupsQuery)
  const {
    data: segments,
    isFetching: isSegmentsFetching,
    refetch: refetchSegments,
  } = useGetSegmentOptions(segmentsQuery)

  const handleClick = React.useCallback(
    (value: string) => () =>
      onFilterChange({
        name: tab,
        value,
      }),
    [tab, onFilterChange],
  )
  const handleCopy = React.useCallback((id: string) => onCopy(Number(id)), [onCopy])
  const handleDelete = React.useCallback(
    async (id: string) => {
      if (isDeleting) return

      const type = tab === GroupsTab.Segments ? 'segment' : 'group'
      const confirmed = await createConfirmation({
        confirmButtonLabel: 'Delete',
        content: `Are you sure you want to delete the selected ${type}? (Contacts inside the ${type} will not be deleted)`,
        title: 'Warning',
      })
      if (!confirmed) return

      const success = await deleteGroup(Number(id))
      if (success) onDeletedRef.current(tab, Number(id))
    },
    [isDeleting, tab, createConfirmation, deleteGroup],
  )
  const handleEdit = React.useCallback(
    (id: string) => {
      onEdit(tab, Number(id))
    },
    [tab, onEdit],
  )
  const handleSearch = React.useCallback(
    (value: string) => {
      if (tab === GroupsTab.Groups) {
        setGroupsQuery((q) => ({
          ...q,
          search: value,
        }))
      } else {
        setSegmentsQuery((q) => ({
          ...q,
          search: value,
        }))
      }
    },
    [tab],
  )
  const debouncedHandleSearch = useDebouncedFn(handleSearch, 300)
  const handleRefresh = React.useCallback(() => {
    if (tab === GroupsTab.Groups) {
      refetchGroups()
    } else {
      refetchSegments()
    }
  }, [refetchGroups, refetchSegments, tab])

  const list = tab === GroupsTab.Groups ? groups : segments
  const tabSearchTerm = (tab === GroupsTab.Groups ? groupsQuery.search : segmentsQuery.search) || ''

  return (
    <Loading isLoading={isDeleting || isGroupsFetching || isSegmentsFetching}>
      <Tabs
        value={tab}
        onChange={(_, newTab) => setTab(newTab)}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        aria-label="groups segments drawer"
      >
        <Tab
          className="groups-tab-btn"
          label={
            <StyledBadge color="primary" badgeContent={groups.length} showZero>
              Groups
            </StyledBadge>
          }
          value="groups"
          {...a11yProps(0)}
        />
        <Tab
          className="segments-tab-btn"
          label={
            <StyledBadge color="primary" badgeContent={segments.length} showZero>
              Segments
            </StyledBadge>
          }
          value="segments"
          {...a11yProps(1)}
        />
      </Tabs>

      {list.length > 0 || tabSearchTerm ? (
        <GroupList
          key={tab}
          defaultSearchTerm={tabSearchTerm}
          disabled={isDeleting}
          groups={list}
          selected={selected}
          tab={tab}
          onClick={handleClick}
          onCopy={handleCopy}
          onDelete={handleDelete}
          onEdit={handleEdit}
          onRefresh={handleRefresh}
          onSearch={debouncedHandleSearch}
        />
      ) : (
        <div
          style={{
            textAlign: 'center',
            paddingTop: '20px',
            paddingBottom: '20px',
          }}
        >
          <Button
            id="add-group-button"
            color="primary"
            variant="contained"
            size="large"
            style={{
              marginRight: '15px',
            }}
            onClick={() => onAdd(tab)}
            startIcon={<Icon>add</Icon>}
          >
            Add your first {tab === 'groups' ? 'group' : 'segment'}
          </Button>
        </div>
      )}
    </Loading>
  )
}

const DrawerListWrapper = styled('div')(({ theme }) => ({
  width: '100%',
  height: 'calc(100vh - 225px)',
  maxWidth: 360,
  backgroundColor: theme.palette.highlight.light,
  overflow: 'hidden',
}))

const GroupSearchContainer = styled('div')(() => ({
  padding: '10px 10px 5px',
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
}))

const SearchBarWrapper = styled('div')(() => ({
  flex: 1,
}))

const ROW_HEIGHT = 70

const StyledListItem = styled(ListItem)(() => ({
  backgroundColor: '#fff',
  padding: 0,
}))

const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  height: '100%',
  padding: theme.spacing(0.5, 2),
}))

const PrimaryLabel = styled('span')(({ theme }) => ({
  display: '-webkit-box',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 2,
  paddingRight: theme.spacing(2),
}))

const a11yProps = (index: number): { id: string; 'aria-controls': string } => ({
  id: `full-width-tab-${index}`,
  'aria-controls': `full-width-tabpanel-${index}`,
})

export default Groups
